import { HttpErrorResponse } from '@angular/common/http';

const HTTP_STATUS_PHRASES = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'method not allowed',
  'conflict',
  'payload too large',
  'unsupported media type',
  'unprocessable entity',
  'internal server error',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
]);

/**
 * Extrait un message métier depuis une erreur HTTP, sinon {@code fallback}.
 * Gère le corps {@code { error }} (BusinessException) et {@code { message }}
 * (PresentationException / Spring Boot).
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof HttpErrorResponse)) {
    return fallback;
  }

  const body = err.error;
  if (typeof body === 'string') {
    const trimmed = body.trim();
    return trimmed && !isHttpStatusPhrase(trimmed) ? trimmed : fallback;
  }

  if (body instanceof Blob) {
    return fallback;
  }

  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const fromMessage = asUsableMessage(record['message']);
    if (fromMessage) {
      return fromMessage;
    }
    const fromError = asUsableMessage(record['error']);
    if (fromError) {
      return fromError;
    }
  }

  return fallback;
}

/**
 * Comme {@link apiErrorMessage}, avec lecture du corps Blob
 * (ex. téléchargement `responseType: 'blob'`).
 */
export async function apiErrorMessageAsync(
  err: unknown,
  fallback: string
): Promise<string> {
  if (
    err instanceof HttpErrorResponse &&
    typeof Blob !== 'undefined' &&
    err.error instanceof Blob
  ) {
    try {
      const text = (await err.error.text()).trim();
      if (!text) {
        return fallback;
      }
      try {
        const parsed = JSON.parse(text) as unknown;
        return apiErrorMessage(
          new HttpErrorResponse({
            error: parsed,
            status: err.status,
            url: err.url ?? undefined,
          }),
          fallback
        );
      } catch {
        return apiErrorMessage(
          new HttpErrorResponse({
            error: text,
            status: err.status,
            url: err.url ?? undefined,
          }),
          fallback
        );
      }
    } catch {
      return fallback;
    }
  }

  return apiErrorMessage(err, fallback);
}

function asUsableMessage(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || isHttpStatusPhrase(trimmed)) {
    return null;
  }
  return trimmed;
}

function isHttpStatusPhrase(value: string): boolean {
  return HTTP_STATUS_PHRASES.has(value.toLowerCase());
}
